import axios, { AxiosHeaders } from "axios"
import packageJson from "../../package.json"
import {
    BASIC_PASSWORD,
    BASIC_USERNAME,
    ENABLE_CLIENT_TOKEN,
    REFRESH_TOKEN_MAX_ITERATE,
    URL_BACKEND_AUTH,
} from "../app/AppConst"
import {
    AUTH_BASIC,
    AUTH_BEARER,
    GRAND_CLIENT_CREDENTIALS,
    GRANT_PASSWORD,
    GRANT_REFRESH_TOKEN,
    HEAD_FB_T,
    HEAD_GRAPHQL,
    HEAD_JSON,
    HEAD_MULTIPART,
    HEAD_URLENCODED,
    HEADER_APP_VERSION,
    HEADER_AUTH,
    HEADER_CONTENT,
    PARAM_GRANT,
    PARAM_PASSWORD,
    PARAM_REFRESH_TOKEN,
    PARAM_USERNAME,
} from "../app/Types"
import AuthUtility from "./AuthUtility"
import BadResponseError from "../dto/base/BadResponseError"
import RefreshTokenExpError from "../dto/base/RefreshTokenExpError"
import type TokenResponseDto from "../dto/base/TokenResponseDto"
import StringUtility from "../util/StringUtility"
import { ErrorMessageResponseDto } from "../dto/base/ErrorMessageResponseDto"

const interceptorWhitelist = [
    URL_BACKEND_AUTH
]

// =================================================== //
export const getAvailableStoredToken = async () => {
    let token: string
    if (AuthUtility.isUserLoggedIn()) {
        token = AuthUtility.getUserCredential().accessToken
    } else {
        token = AuthUtility.getClientCredential()
        if (token === "") {
            await ApiUtility.getClientAccessToken()
            token = AuthUtility.getClientCredential()
        }
    }
    return token
}

//** Axios instance */
export const ApiClient = () => {
    // validate all http response
    const instance = axios.create({
        validateStatus: (status) => true,
        timeout: 30000,
    })

    // REQUEST INTERCEPTOR
    instance.interceptors.request.use(
        async (config) => {
            const isListed = interceptorWhitelist.find((item) => item === config.url)

            if (!isListed) {
                if (ENABLE_CLIENT_TOKEN) {
                    const token = await getAvailableStoredToken()
                    config.headers = {
                        ...config.headers,
                        [HEADER_AUTH]: `${AUTH_BEARER} ${token}`,
                        [HEADER_APP_VERSION]: `${packageJson.version}`,
                    }
                } else if (AuthUtility.isUserLoggedIn()) {
                    const token = await getAvailableStoredToken()
                    config.headers = {
                        ...config.headers,
                        [HEADER_AUTH]: `${AUTH_BEARER} ${token}`,
                        [HEADER_APP_VERSION]: `${packageJson.version}`,
                    }
                }
            }
            return config
        },
        (error) => Promise.reject(error),
    )

    // RESPONSE INTERCEPTOR
    instance.interceptors.response.use(
        async (response) => {
            const isListed = interceptorWhitelist.find(
                (item) => item === response.config.url,
            )

            if (isListed) {
                return response
            }
            if (response.status == 200) {

                //  const parsedResponse = JSON.parse(
                //  JSON.stringify(response.data),
                //  ) as MessageResponseDto
                //  if (parsedResponse.msg !== undefined) {
                //  const isAccessDenied = parsedResponse.msg
                //  .toLocaleLowerCase()
                //  .includes("access is denied")

                const isAccessDenied = false;

                if (isAccessDenied) {
                    await AuthUtility.deleteUserCredential()
                    await AuthUtility.deleteClientCredential()
                    // storeAccessor.dispatch(checkUser())
                    return Promise.reject()
                }
                return response
            }
            if (response.status === 401) {
                if (AuthUtility.isUserLoggedIn()) {
                    const refreshToken = AuthUtility.getUserCredential().refreshToken
                    await ApiUtility.getRefreshToken(refreshToken)
                    response.config.headers = ApiUtility.getRestHeader()
                    return instance.request(response.config)
                    // try {
                    // await ApiUtility.getRefreshToken(refreshToken);
                    // } catch (ex) {
                    // UserUtility.deleteUserCredential();
                    // if (ENABLE_CLIENT_TOKEN) {
                    // await ApiUtility.getClientAccessToken();
                    // }
                    // }

                    // response.config.headers = ApiUtility.getRestHeader();
                    // return instance.request(response.config);
                }
                if (ENABLE_CLIENT_TOKEN) {
                    await ApiUtility.getClientAccessToken()
                    response.config.headers = ApiUtility.getRestHeader()
                }
                return instance.request(response.config)
            }
            if (response.status === 400) {
                if (AuthUtility.isUserLoggedIn()) {
                    await AuthUtility.deleteUserCredential()
                } else {
                    await AuthUtility.deleteClientCredential()
                }
            }
            return response
        },
        (error) => Promise.reject(error))
    return instance
}

// ================================================================== //
export class ApiUtility {
    static tokenClientMutexLock = false
    static tokenUserMutexLock = false
    static lockWaitDuration = 500

    // ================================================================== //
    static getAccessTokenConfig = () => {
        if (AuthUtility.isUserLoggedIn()) {
            return {
                headers: {
                    [HEADER_AUTH]: `${AUTH_BASIC} ${window.btoa(
                        `${BASIC_USERNAME}:${BASIC_PASSWORD}`,
                    )}`,
                    [HEADER_CONTENT]: `${HEAD_URLENCODED}`,
                },
            }
        }

        return ApiUtility.getClientTokenConfig()
    }

    // ================================================================== //
    static getClientTokenConfig = () => {
        return {
            headers: {
                [HEADER_AUTH]: `${AUTH_BASIC} ${window.btoa(
                    `${BASIC_USERNAME}:${BASIC_PASSWORD}`,
                )}`,
                [HEADER_CONTENT]: `${HEAD_URLENCODED}`,
            },
        }
    }

    // ================================================================== //
    static getRestHeader = () => {
        return {
            [HEADER_CONTENT]: `${HEAD_JSON}`,
        }
    }

    // ================================================================== //
    static getRestHeaderQPay = (token: string) => {
        return {
            [HEADER_CONTENT]: `${HEAD_JSON}`,
            [HEADER_AUTH]: `${AUTH_BEARER} ${token}`,
        }
    }

    // ================================================================== //
    static getMultipartDataHeader = () => {
        return {
            [HEADER_CONTENT]: `${HEAD_MULTIPART}`,
        }
    }

    // ================================================================== //
    static getGraphQLHeader = () => {
        return {
            [HEADER_CONTENT]: `${HEAD_GRAPHQL}`,
        }
    }

    // ================================================================== //
    static handleErrorMessage = (errorResponse: ErrorMessageResponseDto) => {
        if (errorResponse.errors) {
            if (errorResponse.errors.length > 0) {
                return errorResponse.errors[0].defaultUserMessage;
            }
        } else {
            return errorResponse.defaultUserMessage
        }

        return "error.general"
    }

    /* ============================================================================ */
    /* ============================================================================ */
    static getClientAccessToken: any = async (
        fromCache?: boolean,
        failedCount?: number,
    ) => {
        if (failedCount === undefined) failedCount = 0
        if (fromCache === undefined) fromCache = false

        if (failedCount > REFRESH_TOKEN_MAX_ITERATE) {
            throw new RefreshTokenExpError()
        }

        if (ApiUtility.tokenClientMutexLock) {
            await new Promise((f) => setTimeout(f, ApiUtility.lockWaitDuration))
            return await this.getClientAccessToken(true, failedCount + 1)
        }
        if (fromCache) {
            return await AuthUtility.getClientCredential()
        }
        ApiUtility.tokenClientMutexLock = true

        const config = ApiUtility.getAccessTokenConfig()
        const params = new URLSearchParams()
        params.append(PARAM_GRANT, GRAND_CLIENT_CREDENTIALS)
        const response = await ApiClient().post<TokenResponseDto>(
            URL_BACKEND_AUTH,
            params,
            config,
        )

        ApiUtility.tokenClientMutexLock = false
        if (response.status >= 200 && response.status < 300) {
            AuthUtility.saveClientCredential(response.data.access_token)
            return response.data.access_token
        }
        if (response.status === 401) {
            throw new BadResponseError(
                response.status,
                response.data.error_description,
            )
        }
        throw new BadResponseError(response.status)
    }

    /* ============================================================================ */
    /* ============================================================================ */
    static getUserAccessToken = async (username: string, password: string) => {
        const config = ApiUtility.getAccessTokenConfig()

        const params = new URLSearchParams()
        params.append(PARAM_USERNAME, username)
        params.append(PARAM_PASSWORD, password)
        params.append(PARAM_GRANT, GRANT_PASSWORD)

        const response = await ApiClient().post<TokenResponseDto>(
            URL_BACKEND_AUTH,
            params,
            config,
        )

        if (response.status >= 200 && response.status < 300) {
            return response.data
        }
        if (response.status === 401) {
            throw new BadResponseError(
                response.status,
                response.data.error_description,
            )
        }
        if (response.status === 400 && response.data.error === "invalid_grant") {
            throw new BadResponseError(
                response.status,
                "Хэрэглэгчийн нэвтрэх нэр эсвэл нууц үг буруу байна",
            )
        }
        throw new BadResponseError(response.status)
    }

    /* ============================================================================ */
    /* ============================================================================ */
    static getNewUserTokenByRefresh = async (refreshToken: string) => {
        const config = ApiUtility.getAccessTokenConfig()

        const params = new URLSearchParams()
        params.append(PARAM_REFRESH_TOKEN, refreshToken)
        params.append(PARAM_GRANT, GRANT_REFRESH_TOKEN)

        // console.log("Getting refresh token");

        const response = await ApiClient().post<TokenResponseDto>(
            URL_BACKEND_AUTH,
            params,
            config,
        )

        // console.log("=============================");
        // console.log(response.data);

        if (response.status >= 200 && response.status < 300) {
            if (
                StringUtility.isValidText(response.data.access_token) &&
                StringUtility.isValidText(response.data.refresh_token)
            ) {
                return response.data
            }
            throw new RefreshTokenExpError()
        }
        if (response.status === 400) {
            throw new RefreshTokenExpError()
        }
        if (response.status === 401) {
            throw new RefreshTokenExpError()
        }
        throw new BadResponseError(response.status)
    }

    /* ============================================================================ */
    /* ============================================================================ */
    static getRefreshToken: any = async (
        refreshToken: string,
        fromCache?: boolean,
        failedCount?: number,
    ) => {
        if (!StringUtility.isValidText(refreshToken)) {
            await AuthUtility.deleteUserCredential()
            throw Error("null")
        }

        if (failedCount === undefined) failedCount = 0
        if (fromCache === undefined) fromCache = false
        if (failedCount > REFRESH_TOKEN_MAX_ITERATE) {
            throw new RefreshTokenExpError()
        }

        if (ApiUtility.tokenUserMutexLock) {
            await new Promise((f) => setTimeout(f, ApiUtility.lockWaitDuration))
            return await this.getRefreshToken(refreshToken, true, failedCount + 1)
        }
        if (fromCache) {
            return await AuthUtility.getUserCredential()
        }
        ApiUtility.tokenUserMutexLock = true

        try {
            const token = await this.getNewUserTokenByRefresh(refreshToken)
            AuthUtility.saveUserCredential({
                accessToken: token.access_token,
                refreshToken: token.refresh_token,
            })

            ApiUtility.tokenUserMutexLock = false
            return {
                accessToken: token.access_token,
                refreshToken: token.refresh_token,
            }
        } catch (error) {
            await AuthUtility.deleteUserCredential()
            await AuthUtility.deleteClientCredential()
            ApiUtility.tokenUserMutexLock = false
        }
    }

    static fetchAuthImage = async (url: string) => {
        try {
            const response = await ApiClient().get<Blob>(url)
            if (response.status >= 200 || response.status < 300) {
                return URL.createObjectURL(response.data)
            }

            throw new BadResponseError(response.status)
        } catch (error) {
            console.error("Error fetching authenticated image:", error)
            throw new BadResponseError(0)
        }
    }

    static loadImageWithAuth = async (fileUuid?: string): Promise<string | undefined> => {
        console.log("Loading image, fileUuid =", fileUuid);
      
        if (!fileUuid) {
          console.warn("fileUuid is empty, skip image");
          return undefined;
        }
      
        try {
          const token = getAvailableStoredToken();
          console.log("Token =", token);
      
          const url = `https://ns9.checkin.mn/v1/file/api/files/${fileUuid}`;
          console.log("Fetching URL =", url);
      
          const res = await fetch(url, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
      
          if (!res.ok) {
            console.error("Failed to load image", res.status);
            return undefined;
          }
      
          const blob = await res.blob();
          return URL.createObjectURL(blob);
      
        } catch (e) {
          console.error("Image fetch failed", e);
          return undefined;
        }
      };
}